import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import type { MrnOrgResolutionContext } from '@/services/mrnOrganizationLookup'
import { isImdbOrgUuid, resolveImdbOrgIdForOrderingApi } from '@/utils/orderingOrgId'
import * as Sentry from '@sentry/react'

/** Which identifier the server matched on, so the copy can stay honest about it. */
export type ExistingPatientMatchedOn = 'mrn' | 'name_dob'

/**
 * One existing order, described well enough to warn a provider off creating a
 * duplicate. Mirrors ``ExistingPatientMatchResponse`` in provider-api.
 */
export interface ExistingPatientMatch {
  order_id: string
  status: string
  matched_on: ExistingPatientMatchedOn
  /** Proband `family_member` id — the id order URLs are built from. */
  patient_id: string | null
  patient_first_name: string | null
  patient_last_name: string | null
  patient_date_of_birth: string | null
  patient_sex: string | null
  patient_mrn: string | null
  sequencing_name: string | null
  created_at: string | null
  ordering_session_id: string | null
}

interface OptionalValue<T> {
  present: boolean
  value: T | null
}

export interface FindExistingPatientParams {
  organizationId: string
  mrn?: string
  firstName?: string
  lastName?: string
  /** ``YYYY-MM-DD``, as held by the new-order form. */
  dateOfBirth?: string
  signal?: AbortSignal
  orgResolution?: MrnOrgResolutionContext
}

/**
 * Look for an existing order matching what the provider has typed so far.
 *
 * POST, not GET: the body carries MRN, name and DOB, which must not land in a
 * URL or an access log.
 *
 * An MRN is authoritative within an organization, so when one is present it is
 * sent alone; name + DOB is only consulted when no MRN has been entered. That
 * keeps a legitimate name collision from shadowing a genuinely new patient —
 * entering a distinct MRN always dismisses the prompt.
 *
 * Resolves to ``null`` whenever no usable answer exists — no match, too little
 * input to identify anyone, or no organization context. A duplicate warning is
 * an optimisation on top of the submit-time uniqueness check, so a failure here
 * must never block the provider or surface an error in the form.
 */
export async function findExistingPatientMatch(
  params: FindExistingPatientParams
): Promise<ExistingPatientMatch | null> {
  const mrn = params.mrn?.trim() ?? ''
  const firstName = params.firstName?.trim() ?? ''
  const lastName = params.lastName?.trim() ?? ''
  const dateOfBirth = params.dateOfBirth?.trim() ?? ''
  const hasNameDob = Boolean(firstName && lastName && dateOfBirth)

  if (!mrn && !hasNameDob) return null

  let orgId = params.organizationId.trim()
  if (!isImdbOrgUuid(orgId) && params.orgResolution) {
    orgId = (
      await resolveImdbOrgIdForOrderingApi({
        orgIdFromAuth: params.orgResolution.orgIdFromAuth,
        additionalUuidCandidate: params.orgResolution.additionalUuidCandidate,
        isEpicFlow: params.orgResolution.isEpicFlow,
        epicOAuthState: params.orgResolution.epicOAuthState ?? null,
      })
    ).trim()
  }
  if (!orgId) return null

  return Sentry.startSpan(
    {
      op: 'http.client',
      name: 'POST /database/organizations/{org_id}/existing-patient-match',
    },
    async (span) => {
      const client = ProviderAPIClient.getInstance()
      const path = `/database/organizations/${encodeURIComponent(orgId)}/existing-patient-match`
      const body: Record<string, string> = {}
      if (mrn) {
        body['mrn'] = mrn
      } else {
        body['first_name'] = firstName
        body['last_name'] = lastName
        body['date_of_birth'] = dateOfBirth
      }

      const response = await client.post(path, body, undefined, true, params.signal)
      span.setAttribute('http.status_code', response.status)

      if (!response.ok) {
        if (response.status >= 500) {
          Sentry.captureMessage('existing-patient-match lookup failed', {
            level: 'warning',
            extra: { status: response.status },
          })
        }
        return null
      }

      const data = (await response.json()) as OptionalValue<ExistingPatientMatch>
      span.setAttribute('match_present', data.present)
      return data.present && data.value ? data.value : null
    }
  )
}
